/**
 * Quests: the spine that pulls players through the valley.
 *
 * A quest is a list of steps. Each step has one goal the server can check, and the words shown
 * on screen while you're on it. Steps that happen at a person (talk, bring) are completed in
 * conversation with them; the rest (have, kill, reach) are noticed by the server as you play.
 * Everything here is data: adding a quest means adding an entry, not code.
 */
import type { FactionId } from './factions.js';
import type { ItemId } from './items.js';
import type { SkillId } from './skills.js';

/**
 * Where a step happens, so the client can point at it: a person (npc kind), a map object (by kind,
 * optionally by name), a kind of creature, or a region.
 */
export interface StepWhere { npc?: string; obj?: string; name?: string; mob?: string; region?: string }

export type QuestStep = StepBody & { where?: StepWhere };
type StepBody =
  /** Talk to someone. `ask` is the line you say; `say` is their answer. */
  | { kind: 'talk'; npc: string; text: string; ask: string; say: string }
  /** Hand someone items from your pack. */
  | { kind: 'bring'; npc: string; item: ItemId; n: number; text: string; ask: string; say: string }
  /** Have items in your pack (gathered, made or bought). */
  | { kind: 'have'; item: ItemId; n: number; text: string }
  /** Kill creatures (or people) of a kind. */
  | { kind: 'kill'; mob: string; n: number; text: string }
  /** Go somewhere: a region, or within `r` tiles of a place. */
  | { kind: 'reach'; region?: string; at?: [number, number]; near?: { obj: string; name?: string }; r?: number; text: string }
  /** Something happened that set a flag (searched a wreck, boarded the ferry, touched a waystone). */
  | { kind: 'flag'; flag: string; /** Any of these will do instead. */ or?: string[]; text: string }
  /** Sell `n` things to a trader. */
  | { kind: 'sell'; n: number; text: string }
  /** Be wearing or wielding something (by its base id: 'bronze_sword'). */
  | { kind: 'wear'; item: string; text: string }
  /** Have something in the chest (`item`, or anything at all). */
  | { kind: 'banked'; item?: ItemId; text: string };

export interface QuestReward {
  xp?: Partial<Record<SkillId, number>>;
  items?: [ItemId, number][];
  coins?: number;
  standing?: Partial<Record<FactionId, number>>;
  /** Permanent unlocks (shortcuts, shops, story beats), by name. */
  flags?: string[];
  /** The quest that starts as soon as this one ends (a questline). */
  starts?: string;
}

export interface QuestDef {
  name: string;
  /** Started for you (a tutorial, or the next quest in a line), never offered in conversation. */
  auto?: boolean;
  /** Who starts it. */
  giver: string;
  /** One line for the quest list. */
  blurb: string;
  /** The option you pick to ask about it. */
  offer: string;
  /** What the giver says when you take it on. */
  intro: string;
  /** Needed before it's offered. */
  requires?: { skills?: Partial<Record<SkillId, number>>; quests?: string[] };
  steps: QuestStep[];
  /** What the last person says when it's done. */
  outro: string;
  reward: QuestReward;
  /** Shown in the reward summary, e.g. "Hal will sell you oak". */
  rewardText?: string[];
  /**
   * Another way through a step: someone who'll sell you what the step needs. Offered in
   * conversation with `npc` while you're on step `step`.
   */
  deals?: QuestDeal[];
  /** The main questline: shown first in the Journal. */
  main?: boolean;
}
export interface QuestDeal { npc: string; step: number; ask: string; say: string; cost: { coins?: number; items?: [ItemId, number][] }; gives: [ItemId, number][] }

export const QUESTS: Record<string, QuestDef> = {
  gull_rock: {
    name: 'Gull Rock',
    auto: true,
    giver: 'tobin',
    blurb: 'Washed up on an island off the valley. Get your bearings, and get across.',
    offer: '',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'tobin', text: 'Click the ground to walk. Go and talk to Tobin', ask: 'Where am I?', say: "Gull Rock, and you're lucky to be on it. That wreck came in with you. Have a look through it: you'll want whatever's left.", where: { npc: 'tobin' } },
      { kind: 'flag', flag: 'gull_wreck', text: 'Search the wreck on the beach', where: { obj: 'cache' } },
      { kind: 'have', item: 'raw_minnow', n: 3, text: 'Net 3 minnows: click the ripples off the shore', where: { obj: 'shore' } },
      { kind: 'have', item: 'cooked_minnow', n: 2, text: 'Cook 2 at the pier fire (some may burn)', where: { obj: 'campfire', name: 'Pier fire' } },
      { kind: 'have', item: 'birch_log', n: 3, text: 'Cut 3 birch logs in the scrub', where: { obj: 'birch' } },
      { kind: 'have', item: 'copper_ore', n: 2, text: 'Dig 2 copper ore from the orange-flecked rocks', where: { obj: 'copper' } },
      { kind: 'have', item: 'tin_ore', n: 2, text: 'Dig 2 tin ore from the grey-flecked rocks', where: { obj: 'tin' } },
      { kind: 'have', item: 'bronze_bar', n: 2, text: 'Smelt 2 bronze bars at the furnace (each takes a log)', where: { obj: 'furnace' } },
      { kind: 'have', item: 'bronze_hatchet', n: 1, text: 'Hammer a bronze hatchet at the anvil', where: { obj: 'anvil' } },
      { kind: 'kill', mob: 'dog', n: 1, text: 'Drive off a feral dog: click it to fight, click food to eat', where: { mob: 'dog' } },
      { kind: 'kill', mob: 'looter', n: 1, text: 'Stop the looter going through the terminal', where: { mob: 'looter' } },
      { kind: 'banked', text: 'Put something in the lockbox for safekeeping', where: { obj: 'chest', name: 'Lockbox' } },
      { kind: 'sell', n: 1, text: 'Sell something to Sal, the Drift trader', where: { npc: 'sal' } },
      { kind: 'talk', npc: 'tobin', text: 'Tell Tobin you are ready to go', ask: "I'm ready for the mainland.", say: "Then the ferry's yours. Listen, though: over there, if you fall, you keep the three best things you carry. The rest waits in a grave where you dropped, and it's yours alone for fifteen minutes. After that, anyone's. So don't wander where you can't come back from.", where: { npc: 'tobin' } },
      { kind: 'flag', flag: 'ferried', text: 'Board the ferry to the mainland', where: { obj: 'ferry' } },
    ],
    outro: 'The ferry noses into the Landing. Sunderfen.',
    reward: { coins: 25, flags: ['tutorial_done'], starts: 'settling_in' },
    rewardText: ['Find your feet in Meadowfold'],
  },
  // ---------------- Settling In: Meadowfold's people, one after another ----------------
  settling_in: {
    name: 'Settling In',
    auto: true,
    giver: 'tamsin',
    blurb: 'Off the ferry with a pack and not much else. Find your feet in Meadowfold.',
    offer: '',
    intro: '',
    steps: [
      { kind: 'flag', flag: 'waystone:the landing', text: 'Touch the waystone at the Landing: you can travel between any you have touched', where: { obj: 'waystone', name: 'The Landing' } },
      { kind: 'reach', region: 'village', text: 'Follow the road north to Meadowfold', where: { region: 'village' } },
      { kind: 'talk', npc: 'tamsin', text: 'Find Tamsin, the village elder', ask: 'Tobin sent me over from Gull Rock.', say: "Did he. Then you'll want a roof and a reason to stay. Meadowfold has both, if you earn them. Start with the fence on the Downs.", where: { npc: 'tamsin' } },
    ],
    outro: "Start with the fence on the Downs.",
    reward: { flags: ['settled'], starts: 'mending_fences' },
  },
  mending_fences: {
    name: 'Mending Fences',
    giver: 'tamsin',
    blurb: 'Something keeps getting into the Downs cabbage rows. Tamsin wants the fence fixed.',
    offer: 'Is there anything I can do around here?',
    intro: "There is. Something's been at the cabbages on the Downs, and the fence is more gap than fence. Hal knows timber; ask him what it needs.",
    steps: [
      { kind: 'talk', npc: 'tamsin', text: 'Ask Tamsin about the fence', ask: 'What happened to the fence?', say: "Rabbits, wind, and nobody mending it. Hal knows timber; ask him what it needs.", where: { npc: 'tamsin' } },
      { kind: 'talk', npc: 'hal', text: 'Ask Hal the woodcutter, west in Birchwood, what the fence needs', ask: 'Tamsin sent me about the Downs fence.', say: "Birch. Three good lengths will patch it. The trees just outside the village are fine; mind your fingers.", where: { npc: 'hal' } },
      { kind: 'have', item: 'birch_log', n: 3, text: 'Cut 3 birch logs', where: { obj: 'birch' } },
      { kind: 'bring', npc: 'tamsin', item: 'birch_log', n: 3, text: 'Bring the 3 birch logs to Tamsin', ask: "Here's the timber for the fence.", say: "That'll do nicely. Now, the culprits are still out there. Rabbits, fat on our cabbages.", where: { npc: 'tamsin' } },
      { kind: 'kill', mob: 'rabbit', n: 2, text: 'Deal with 2 rabbits on the Downs', where: { mob: 'rabbit' } },
      { kind: 'reach', region: 'downs', text: 'Walk the fence line on Thistle Downs', where: { region: 'downs' } },
      { kind: 'talk', npc: 'tamsin', text: 'Tell Tamsin the fence is done', ask: "The fence is up and the rabbits won't be back.", say: '', where: { npc: 'tamsin' } },
    ],
    outro: "Good. You're useful, and people here notice useful. Bram's been cursing at his forge all week; go and see if he'll let you help.",
    reward: { xp: { forestry: 150, melee: 100 }, coins: 40, standing: { settlers: 50 }, flags: ['fences_mended'], starts: 'bram_bellows' },
    rewardText: ['The settlers think better of you'],
  },
  bram_bellows: {
    name: "Bram's Bellows",
    giver: 'bram',
    requires: { quests: ['mending_fences'] },
    blurb: "Bram's forge bellows are broken, and he's too proud to say so.",
    offer: 'Tamsin says you could use a hand.',
    intro: "Could I. The bellows frame split. I need two bronze bars for the brackets and four birch logs for the frame.",
    steps: [
      { kind: 'talk', npc: 'bram', text: 'See what Bram the smith needs', ask: 'Tamsin says you could use a hand.', say: "Could I. The bellows frame split. I need two bronze bars for the brackets and four birch logs for the frame. Copper and tin are in the rocks on the Downs.", where: { npc: 'bram' } },
      { kind: 'have', item: 'bronze_bar', n: 2, text: 'Smelt 2 bronze bars (copper and tin from the Downs, at the furnace)', where: { obj: 'furnace' } },
      { kind: 'bring', npc: 'bram', item: 'bronze_bar', n: 2, text: 'Bring Bram the 2 bronze bars', ask: 'Two bronze bars, as promised.', say: "Clean enough. Now the timber.", where: { npc: 'bram' } },
      { kind: 'have', item: 'birch_log', n: 4, text: 'Cut 4 birch logs', where: { obj: 'birch' } },
      { kind: 'bring', npc: 'bram', item: 'birch_log', n: 4, text: 'Bring Bram the 4 birch logs', ask: "And four birch logs for the frame.", say: '', where: { npc: 'bram' } },
    ],
    outro: "Listen to that. She breathes again. Here: a sword I made too short for anyone in this village. It'll fit you. Now go and see Wenna; she'll want to feed you.",
    reward: { xp: { smithing: 250, mining: 120 }, coins: 40, items: [['bronze_sword', 1]], standing: { settlers: 30 }, starts: 'wennas_table' },
    rewardText: ["Bram's spare bronze sword"],
  },
  wennas_table: {
    name: "Wenna's Table",
    giver: 'wenna',
    requires: { quests: ['bram_bellows'] },
    blurb: 'Wenna feeds half the village. She is short on fish and onions.',
    offer: 'Bram says you feed half the village.',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'wenna', text: 'Ask Wenna the cook what she needs', ask: 'Bram says you feed half the village.', say: "The whole village. I'm short. Five cooked minnows from the Landing, and three wild onions from the Downs. Maren can show you the fishing.", where: { npc: 'wenna' } },
      { kind: 'have', item: 'cooked_minnow', n: 5, text: 'Net and cook 5 minnows (the Landing has fish and a fire)', where: { obj: 'shore' } },
      { kind: 'have', item: 'wild_onion', n: 3, text: 'Pick 3 wild onions on the Downs', where: { obj: 'onions' } },
      { kind: 'bring', npc: 'wenna', item: 'cooked_minnow', n: 5, text: 'Bring Wenna the 5 cooked minnows', ask: 'Five minnows, cooked.', say: 'Lovely. And the onions?', where: { npc: 'wenna' } },
      { kind: 'bring', npc: 'wenna', item: 'wild_onion', n: 3, text: 'Bring Wenna the 3 wild onions', ask: 'Three onions.', say: '', where: { npc: 'wenna' } },
    ],
    outro: "There. Take a loaf for the road. And go and see Hal: he's been muttering about his woodlot.",
    reward: { xp: { cooking: 250, fishing: 200 }, coins: 20, items: [['bread', 3]], standing: { settlers: 30 }, starts: 'hals_woodlot' },
  },
  hals_woodlot: {
    name: "Hal's Woodlot",
    giver: 'hal',
    requires: { quests: ['wennas_table'] },
    blurb: 'Boar are tearing up the saplings in Hal\'s woodlot, and he is short of timber.',
    offer: "Wenna says you're short-handed.",
    intro: '',
    steps: [
      { kind: 'talk', npc: 'hal', text: 'See what Hal needs in Birchwood', ask: "Wenna says you're short-handed.", say: "Always. A boar's been tearing up my saplings. Drive it off for me (eat if it gets nasty), then cut me six birch logs.", where: { npc: 'hal' } },
      { kind: 'wear', item: 'bronze_sword', text: "Wield Bram's bronze sword: click it in your pack", where: {} },
      { kind: 'kill', mob: 'boar', n: 1, text: 'Drive off a boar in Birchwood (eat food if you get hurt)', where: { mob: 'boar' } },
      { kind: 'have', item: 'birch_log', n: 6, text: 'Cut 6 birch logs', where: { obj: 'birch' } },
      { kind: 'bring', npc: 'hal', item: 'birch_log', n: 6, text: 'Bring Hal the 6 birch logs', ask: 'Six birch logs.', say: '', where: { npc: 'hal' } },
    ],
    outro: "Good arm on you. Odile pays fair for logs, and she hears everything. Go and see her.",
    reward: { xp: { forestry: 350, melee: 200 }, coins: 50, standing: { settlers: 30 }, starts: 'odiles_ledger' },
  },
  odiles_ledger: {
    name: "Odile's Ledger",
    giver: 'odile',
    requires: { quests: ['hals_woodlot'] },
    blurb: 'Odile keeps the village accounts, and something has her worried.',
    offer: 'Hal says you hear everything.',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'odile', text: 'Talk to Odile at her stall', ask: 'Hal says you hear everything.', say: "I hear what people sell me. Sell me three things, and I'll tell you what the valley's short of.", where: { npc: 'odile' } },
      { kind: 'sell', n: 3, text: 'Sell Odile 3 things', where: { npc: 'odile' } },
      { kind: 'talk', npc: 'odile', text: "Ask Odile what's wrong", ask: "There. What's the valley short of?", say: "Everything, since the lake road went quiet. Ysolde's caravan hasn't come down from the lake market in a week. Tamsin knows more than she says.", where: { npc: 'odile' } },
    ],
    outro: "Tamsin knows more than she says. Ask her.",
    reward: { coins: 30, standing: { drift: 20 }, starts: 'word_from_the_lake' },
  },
  word_from_the_lake: {
    name: 'Word from the Lake',
    giver: 'tamsin',
    requires: { quests: ['odiles_ledger'] },
    blurb: "The road north to the lake market has gone quiet. Tamsin wants to know why.",
    offer: "Odile says the lake road's gone quiet.",
    intro: '',
    steps: [
      { kind: 'talk', npc: 'tamsin', text: 'Ask Tamsin about the lake road', ask: "Odile says the lake road's gone quiet.", say: "Quiet's a kind word. Go north up the road, through the gap in the ridge, and look. Only look. Come straight back.", where: { npc: 'tamsin' } },
      { kind: 'reach', near: { obj: 'campfire', name: 'Reaver fire' }, r: 16, text: 'Go north on the lake road, past the ridge, and see what is there (carefully)', where: { obj: 'campfire', name: 'Reaver fire' } },
      { kind: 'talk', npc: 'tamsin', text: 'Tell Tamsin what you saw', ask: 'Reavers. Camped right off the road, three at least.', say: '', where: { npc: 'tamsin' } },
    ],
    outro: "Reavers. On our road. Then it starts again. You did well, and you came back, which is more than some. When you're stronger, that road is yours to open.",
    reward: { xp: { exploration: 300 }, coins: 60, standing: { settlers: 100 }, flags: ['lake_road_closed'], starts: 'what_ended_1' },
    rewardText: ['The lake road is held by Reavers'],
  },

  /* ---------------- middle-band region quests: a camp each, dealt with your way ---------------- */
  the_dig: {
    name: 'The Dig',
    giver: 'corwin',
    requires: { quests: ['settling_in'] },
    blurb: "Reavers have taken over the best face in the Copperback. Corwin wants it back, or at least his survey.",
    offer: 'You look like a man with a problem.',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'corwin', text: 'Ask Corwin what the trouble is', ask: 'You look like a man with a problem.', say: "Reavers. Up on the north face, digging my iron with their prospectors and a foreman who swings a pick like it owes him money. They've my survey book in their stash too: thirty years of where the good seams are. Clear them off, or get in and take the book back. I won't ask which.", where: { npc: 'corwin' } },
      { kind: 'flag', flag: 'cleared:copperback_dig', or: ['stole:copperback_dig'], text: 'Clear the Reavers out of the Copperback dig, or slip into their stash unseen', where: { obj: 'stash', name: 'Copperback dig stash' } },
      { kind: 'talk', npc: 'corwin', text: 'Tell Corwin', ask: "The dig's yours again. For a while.", say: '', where: { npc: 'corwin' } },
    ],
    outro: "For a while is all anything is. Here, for your trouble. And if you're ever up there with a pick, the seam behind the spoil heap runs iron right through.",
    reward: { xp: { mining: 600, exploration: 200 }, coins: 120, standing: { settlers: 30 } },
  },
  false_fire: {
    name: 'False Fire',
    giver: 'maren',
    requires: { quests: ['settling_in'] },
    blurb: "Someone is lighting fires on Saltreach to bring boats onto the rocks. Maren knows who.",
    offer: 'Bad weather for boats lately?',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'maren', text: 'Ask Maren about the boats', ask: 'Bad weather for boats lately?', say: "Weather's fine. It's the fire on Saltreach that's wrong: wreckers, under that upturned hull east along the shore. Boats see a light, think it's the harbour, and go onto the rocks. Saltmouth and his lot pick over what's left. Put the fire out. However you like.", where: { npc: 'maren' } },
      { kind: 'flag', flag: 'cleared:saltreach_wreckers', or: ['stole:saltreach_wreckers'], text: "Deal with the wreckers on Saltreach: clear the hull, or rob them blind without being seen", where: { obj: 'stash', name: "Wreckers' hull stash" } },
      { kind: 'talk', npc: 'maren', text: 'Go back to Maren at the Landing', ask: "The false fire's out.", say: '', where: { npc: 'maren' } },
    ],
    outro: "Then the boats will come in right for a week or two, until Saltmouth finds new friends. That's the shore for you. Take this, and some of my good bait.",
    reward: { xp: { fishing: 500, exploration: 200 }, coins: 100, items: [['bait', 50]], standing: { settlers: 20, drift: 20 } },
  },
  the_overturned_cart: {
    name: 'The Overturned Cart',
    giver: 'hal',
    requires: { quests: ['hals_woodlot'] },
    blurb: "Hal's timber cart never came back from the fen road.",
    offer: "Where's your cart, Hal?",
    intro: '',
    steps: [
      { kind: 'talk', npc: 'hal', text: 'Ask Hal about his cart', ask: "Where's your cart, Hal?", say: "On its side on the fen road, last I heard, and the lad who drove it ran home without his boots. Carts don't tip themselves. If you go, go ready: whatever tipped it is still about.", where: { npc: 'hal' } },
      { kind: 'flag', flag: 'cleared:cart_ambush', text: 'Find the cart on the fen road, and deal with whoever is waiting by it', where: { obj: 'cart_tipped' } },
      { kind: 'talk', npc: 'hal', text: 'Tell Hal what happened', ask: 'Reavers, lying in wait. Not any more.', say: '', where: { npc: 'hal' } },
    ],
    outro: "An ambush. On a timber road. What's left to steal off a timber road? ... Fear, I suppose. Thanks. I'll send two carts next time, and an axe on each.",
    reward: { xp: { forestry: 400, melee: 300 }, coins: 90, standing: { settlers: 30 } },
  },

  /* ---------------- Odd jobs in Meadowfold: short, side by side, any order ---------------- */
  rabbits_in_the_rows: {
    name: 'Rabbits in the Rows',
    giver: 'marlow',
    requires: { quests: ['settling_in'] },
    blurb: "Rabbits are eating Marlow's seedlings faster than he can plant them.",
    offer: 'Your rows look chewed.',
    intro: "Chewed? Mown, more like. There's a warren down past the Downs farm. Thin them out for me, would you? Five should teach the rest some manners.",
    steps: [
      { kind: 'kill', mob: 'rabbit', n: 5, text: 'Deal with 5 rabbits on the Downs', where: { mob: 'rabbit' } },
      { kind: 'talk', npc: 'marlow', text: 'Tell Marlow the rows are safe', ask: "That's five fewer rabbits.", say: '', where: { npc: 'marlow' } },
    ],
    outro: "Five! I'll have carrots this year after all. Here, take some seed: you've earned the first crop.",
    reward: { xp: { melee: 120, farming: 80 }, coins: 30, items: [['carrot_seed', 6]], standing: { settlers: 15 } },
    rewardText: ['A handful of carrot seed'],
  },
  pips_eggs: {
    name: "Pip's Eggs",
    giver: 'pip',
    requires: { quests: ['settling_in'] },
    blurb: "Pip was meant to fetch the eggs and let the hens out instead.",
    offer: 'You look like you lost something.',
    intro: "Mum sent me for eggs and I opened the coop and they all RAN. Can you get me four eggs? The hens wander round the edge of the village. Please don't tell Mum.",
    steps: [
      { kind: 'bring', npc: 'pip', item: 'egg', n: 4, text: 'Bring Pip 4 eggs (the hens round the village drop them)', ask: "Here are your eggs.", say: '', where: { mob: 'chicken' } },
    ],
    outro: "Four! You're the best. I'm not telling Mum about the hens. I'm not telling anyone. Here, it's all my coins.",
    reward: { xp: { melee: 60, cooking: 60 }, coins: 25, standing: { settlers: 15 } },
  },
  wool_for_gilda: {
    name: 'Wool for Gilda',
    giver: 'gilda',
    requires: { quests: ['settling_in'] },
    blurb: 'Gilda the tanner is short of wool for winter linings.',
    offer: 'Busy time of year?',
    intro: "Always, before the cold. I line everything I sell, and I'm out of wool. Four fleeces from the sheep on the Downs and I'll show you how a lining's done.",
    steps: [
      { kind: 'bring', npc: 'gilda', item: 'wool', n: 4, text: 'Bring Gilda 4 wool from the sheep on the Downs', ask: "Four fleeces, as promised.", say: '', where: { mob: 'sheep' } },
    ],
    outro: "Good thick wool. Watch: fold, stitch, turn. That's a lining. You'll be making your own coats before the snow.",
    reward: { xp: { crafting: 200 }, coins: 35, standing: { settlers: 15 } },
  },
  hespers_yarrow: {
    name: "Hesper's Yarrow",
    giver: 'hesper',
    requires: { quests: ['settling_in'] },
    blurb: 'Hesper needs yarrow for mending draughts, and her knees need a rest.',
    offer: 'Anything you need gathering?',
    intro: "Yarrow. White flowers, feathery leaves, all over the Downs. Three bunches. Pull them roots and all and don't mind the dirt: I'll clean them.",
    steps: [
      { kind: 'bring', npc: 'hesper', item: 'grimy_yarrow', n: 3, text: 'Bring Hesper 3 bunches of yarrow from the Downs', ask: "Three bunches of yarrow.", say: '', where: { obj: 'wild_yarrow' } },
    ],
    outro: "Lovely. Now, see how I cut it and steep it? That's a mending draught. Keep one: you'll need it before I do.",
    reward: { xp: { herblore: 150 }, coins: 20, items: [['mending_draught', 1]], standing: { settlers: 15 } },
    rewardText: ['A mending draught'],
  },
  marens_bait_run: {
    name: "Maren's Bait Run",
    giver: 'maren',
    requires: { quests: ['settling_in'], skills: { fishing: 5 } },
    blurb: 'Maren at the Landing wants perch for cutting into bait.',
    offer: 'Are they biting?',
    intro: "Biting, yes. Biting my bait, every last bit of it. Bring me six perch off the lake shallows and I'll cut us both a share.",
    steps: [
      { kind: 'bring', npc: 'maren', item: 'raw_perch', n: 6, text: 'Bring Maren 6 raw perch (rod and bait, lake water)', ask: "Six perch for the bait tub.", say: '', where: { npc: 'maren' } },
    ],
    outro: "Fat ones, too. Here's your share of the bait and a little for your trouble. Fish the evening rise: that's when the big ones come up.",
    reward: { xp: { fishing: 220 }, coins: 40, items: [['bait', 50]], standing: { settlers: 15 } },
    rewardText: ['50 bait'],
  },

  /* ---------------- What Ended: the main questline, one chapter per band ---------------- */
  what_ended_1: {
    name: 'What Ended I: The Ledger',
    main: true,
    auto: true,
    giver: 'tamsin',
    blurb: "The Reavers on the lake road took something from the Drift. Get it back, any way you like.",
    offer: '',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'tamsin', text: 'Ask Tamsin how to open the lake road', ask: 'You said the road was mine to open.', say: "So I did. The Drift had a runner on that road the day it closed, and the Reavers took something off him. Ysolde keeps the Drift's books at the lake market. The road goes right past the outpost; go round by Birchwood and the Copperback if you'd rather not.", where: { npc: 'tamsin' } },
      { kind: 'talk', npc: 'ysolde', text: 'Find Ysolde at the lake market', ask: 'Tamsin sent me. The Reavers took something of yours?', say: "Our ledger. Routes, debts, who owes the Drift a favour, and pages that were old when my grandmother found them. Gristle keeps it in the outpost stash. Fight your way in. Or creep in while they're looking the other way: crouch, keep to cover, and don't run. Or, if you'd sooner keep your skin, Corwin up in the Copperback has dealings with the Reavers. He could buy it back. For a price.", where: { npc: 'ysolde' } },
      { kind: 'have', item: 'drift_ledger', n: 1, text: "Get the Drift's ledger: from the outpost stash (fight, or sneak in), or buy it back through Corwin in the Copperback", where: { obj: 'stash', name: 'Lake-road outpost stash' } },
      { kind: 'bring', npc: 'ysolde', item: 'drift_ledger', n: 1, text: 'Take the ledger back to Ysolde', ask: "Here's your ledger.", say: '', where: { npc: 'ysolde' } },
    ],
    deals: [{ npc: 'corwin', step: 2, ask: "Ysolde says you can get things back from the Reavers.", say: "Can. Did. Gristle's lot like coin more than paper. There: one ledger, smelling of their fire. Don't tell Tamsin where you got it.", cost: { coins: 120 }, gives: [['drift_ledger', 1]] }],
    outro: "All of it? Even the back pages. Look: these aren't ours. A map of the valley from before, the lake drawn smaller, and up in the high country a square marked FACILITY: RESTRICTED. And a list of names under 'Phase 3: evacuation by rail'. Half of Meadowfold's family names are on it. Whatever ended things, it started up there. Keep this copy. The Drift won't forget what you did.",
    reward: { xp: { exploration: 400 }, coins: 150, items: [['burnt_map', 1]], standing: { drift: 80, settlers: 40 }, flags: ['what_ended_1'] },
    rewardText: ['A piece of the mystery: a Facility in the high country'],
  },
  what_ended_2: {
    name: 'What Ended II: The Last Train',
    main: true,
    giver: 'ysolde',
    requires: { quests: ['what_ended_1'] },
    blurb: "'Phase 3: evacuation by rail.' Somebody in Rustcliff remembers the trains. Mother Rook has the rest.",
    offer: "Those names on the back pages. 'Evacuation by rail.' Where did the trains go?",
    intro: '',
    steps: [
      { kind: 'talk', npc: 'ysolde', text: 'Ask Ysolde about the evacuation list', ask: "Those names on the back pages. 'Evacuation by rail.' Where did the trains go?", say: "Up. That's all anyone in Meadowfold will say. But there's an old man past the rockslide in Rustcliff, Orrin, who worked the rail yard before the Reavers had it. If anyone knows where the last train went, he does. It's Reaver country up there. Go ready.", where: { npc: 'ysolde' } },
      { kind: 'talk', npc: 'orrin', text: 'Find Orrin, the old railwayman, in Rustcliff (past the rockslide above the lake)', ask: "Ysolde sent me. Where did the evacuation trains go?", say: "Phase 3. I haven't heard those words said out loud in forty years. I drove the yard engine; the big trains came through without stopping. The stationmaster wrote every one in his log: where it went, who was on it. That log is in Mother Rook's strongbox in the Hold. She burns a page now and then to light her stove. Go in fighting, or go in quiet. Or talk to Vesk at the quarry, if you have the coin. Just get it before she runs out of pages.", where: { npc: 'orrin' } },
      { kind: 'have', item: 'station_log', n: 1, text: "Get the stationmaster's log from Rustcliff Hold: fight Rook's people, slip in unseen at night, or buy it from Vesk at the quarry", where: { obj: 'stash', name: 'Rustcliff Hold stash' } },
      { kind: 'bring', npc: 'orrin', item: 'station_log', n: 1, text: 'Take the log to Orrin', ask: "Here's the stationmaster's log.", say: '', where: { npc: 'orrin' } },
    ],
    deals: [{ npc: 'vesk', step: 2, ask: "Orrin says you can get things out of the Hold.", say: "The stationmaster's book? Rook thinks it's kindling. Coin for me, steel for her stockade, and I'll tell her the stove ate it. There. Now walk away slow, and if anyone asks, you never met me.", cost: { coins: 800, items: [['steel_bar', 5]] }, gives: [['station_log', 1]] }],
    outro: "Most of it's ash. But here, the last full page. 'Special working, Phase 3. All stations to the Facility, high country line, via the Delve cutting. Train seven departed full, 06:10. No return working.' No return working. They never meant to bring them back down. And here, in a different hand, written later: 'Line closed at the cutting. Signal box abandoned. God help them.' The line goes up through the Deep Delve. Whatever's at the end of it, that's where your answer is. Take the page. I don't want it.",
    reward: { xp: { exploration: 1500, melee: 800 }, coins: 600, items: [['last_timetable', 1]], standing: { settlers: 80, drift: 60 }, flags: ['what_ended_2'] },
    rewardText: ['The last train went up through the Delve to the Facility'],
  },

  /* ---------------- outer-band region quests ---------------- */
  the_toll_road: {
    name: 'The Toll Road',
    giver: 'orrin',
    requires: { quests: ['settling_in'] },
    blurb: "A Reaver patrol takes a toll off everyone on the high road out of Rustcliff. Orrin wants it to stop.",
    offer: 'Who walks the high road?',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'orrin', text: 'Ask Orrin about the high road', ask: 'Who walks the high road?', say: "Rook's outrider and two of the yard dogs, one with legs and one with four. Up and down from here to the cutting, taking whatever you carry. The Drift won't send a cart up there any more. Run them off the road and the high country's open again.", where: { npc: 'orrin' } },
      { kind: 'flag', flag: 'cleared:rustcliff_patrol', text: 'Run the Reaver patrol off the high road north of Rustcliff', where: { mob: 'reaver_outrider' } },
      { kind: 'talk', npc: 'orrin', text: 'Tell Orrin the road is clear', ask: "The high road's clear.", say: '', where: { npc: 'orrin' } },
    ],
    outro: "For a while. Rook will send another. But a while is long enough for a cart to get through, and the Drift will remember who made it so. Here: I've no use for coin up here.",
    reward: { xp: { melee: 1200, exploration: 600 }, coins: 300, standing: { settlers: 30, drift: 40 } },
  },
  ironwood_thieves: {
    name: 'Ironwood Thieves',
    giver: 'wren',
    requires: { quests: ['settling_in'] },
    blurb: "Reaver cutters are felling the oldest trees in Hollow Grove. Wren wants them gone.",
    offer: 'Who is cutting the trees?',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'wren', text: 'Ask Wren about the axes in the Grove', ask: 'Who is cutting the trees?', say: "Harl the Feller and his crew, camped north of here. A tree a day, carted back to Rustcliff for Rook's stockade. Drive them out. Or get into their camp unseen and take their saws and their stock: a crew without saws is just men standing in a wood.", where: { npc: 'wren' } },
      { kind: 'flag', flag: 'cleared:grove_cutters', or: ['stole:grove_cutters'], text: "Deal with the cutters' camp in Hollow Grove: clear it, or rob it without being seen", where: { obj: 'stash', name: 'Ironwood cutters stash' } },
      { kind: 'talk', npc: 'wren', text: 'Go back to Wren', ask: 'The axes have stopped.', say: '', where: { npc: 'wren' } },
    ],
    outro: "Listen. Nothing. That's the sound of three hundred years going on a bit longer. Take this, and a cutting from one of the old yews. Plant it somewhere the Reavers will never find it.",
    reward: { xp: { forestry: 2500, exploration: 600 }, coins: 250, items: [['yew_seed', 1]], standing: { settlers: 40 } },
  },
  smugglers_light: {
    name: "The Smugglers' Light",
    giver: 'cass',
    requires: { quests: ['settling_in'] },
    blurb: "Reaver boats land at Stillwater with their lights out. Cass knows where, and wants it stopped.",
    offer: 'Who rows without lights?',
    intro: '',
    steps: [
      { kind: 'talk', npc: 'cass', text: 'Ask Cass about the boats', ask: 'Who rows without lights?', say: "Mags Kettle's lot. They land at the north end of the cove, unload into a camp behind the rocks, and walk it over the hills to Rustcliff. They took my last boat for the run and sank it inside a month. Break up the landing. Or get into the camp unseen and take the run's takings off them; that'll hurt Mags more than a beating.", where: { npc: 'cass' } },
      { kind: 'flag', flag: 'cleared:cove_smugglers', or: ['stole:cove_smugglers'], text: "Deal with the smugglers' landing in Stillwater Cove: clear it, or rob it unseen", where: { obj: 'stash', name: "Smugglers' landing stash" } },
      { kind: 'talk', npc: 'cass', text: 'Go back to Cass', ask: "The landing's done.", say: '', where: { npc: 'cass' } },
    ],
    outro: "Then the next boat I build might float more than a month. Here. And some cedar: you'll know what to do with it.",
    reward: { xp: { fishing: 2000, exploration: 600 }, coins: 300, items: [['cedar_log', 6]], standing: { settlers: 20, drift: 50 } },
  },
};

export const QUEST_IDS = Object.keys(QUESTS);
export const isQuestId = (v: unknown): v is string => typeof v === 'string' && Object.prototype.hasOwnProperty.call(QUESTS, v);
export const questStatus = (c: { quests?: Record<string, { step: number; done?: number }> }, id: string): 'none' | 'active' | 'done' => {
  const q = c.quests?.[id];
  return !q ? 'none' : q.done ? 'done' : 'active';
};

/** Levels for the checks below, without pulling in character code. */
type QuestChar = { quests: Record<string, { done?: number; step: number }>; xp: Partial<Record<SkillId, number>> };
/** Quests this character could pick up now (skills and earlier quests met), by id. `lvl` turns xp into a level. */
export function questsOffered(c: QuestChar, lvl: (skill: SkillId) => number): string[] {
  return Object.keys(QUESTS).filter(id => {
    const d = QUESTS[id];
    if (d.auto || c.quests[id]) return false;
    for (const [k, L] of Object.entries(d.requires?.skills ?? {})) if (lvl(k as SkillId) < (L as number)) return false;
    for (const q of d.requires?.quests ?? []) if (!c.quests[q]?.done) return false;
    return true;
  });
}
/** The people your quests need right now (talk to, hand something to, sell to), with the quest. */
export function questPeople(c: QuestChar): Map<string, string> {
  const out = new Map<string, string>();
  for (const [id, q] of Object.entries(c.quests)) {
    const st = !q.done ? QUESTS[id]?.steps[q.step] : undefined;
    if (!st) continue;
    const who = st.kind === 'talk' || st.kind === 'bring' ? st.npc : st.kind === 'sell' ? st.where?.npc : undefined;
    if (who && !out.has(who)) out.set(who, id);
  }
  return out;
}
